import { useCallback, useState } from "react"

import { toast } from "react-toastify"
import { concat, Observable, of } from "rxjs"
import { catchError, map } from "rxjs/operators"

import NotificationContactData from "./NotificationContactData"
import handleAPIError from "../../../utils/handleAPIError"
import ApiResult from "../../ApiResult"

interface UseNotificationActionProps<T> {
  actionObservable: (props: { PoolId: number; ContactIds: number[] }) => Observable<T>
  callback?: (result?: T) => void
}

function useNotificationAction<T>({
  actionObservable,
  callback,
}: UseNotificationActionProps<T>): [
  processing: Record<number, boolean>,
  performAction: (poolId: number, contacts: NotificationContactData[]) => void,
] {
  const [processing, setProcessing] = useState<Record<number, boolean>>({})

  const performAction = useCallback(
    (poolId: number, contacts: NotificationContactData[]) => {
      const contactIds = contacts.map((c) => c.ContactID)

      const observable = concat(
        of({ loading: true }),
        actionObservable({
          PoolId: poolId,
          ContactIds: contactIds,
        }).pipe(
          map((result) => ({ loading: false, result })),
          catchError((error) => of({ loading: false, result: undefined, error }))
        )
      )

      const subscription = observable.subscribe(({ loading, result, error }: ApiResult<T>) => {
        setProcessing(contactIds.reduce((acc, id) => ({ ...acc, [id]: loading }), {}))
        if (!loading && !error) {
          toast.success(result)
          callback?.(result)
        }
        if (error) {
          handleAPIError(error)
        }
      })

      return () => subscription.unsubscribe()
    },
    [actionObservable, callback]
  )

  return [processing, performAction]
}

export default useNotificationAction
